/**
 * Cliente personalizado para Supabase usando 'fetch' (Sin dependencias externas)
 * VERSION: 8.2 (Standardized Builder Pattern)
 */

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL?.trim();
const SUPABASE_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY?.trim();

export const supabase = {
  // ── AUTENTICACIÓN ──────────────────────────────────────────────────
  auth: {
    signIn: async (email, password) => {
      console.log('🔑 Supabase signIn → URL:', `${SUPABASE_URL}/auth/v1/token?grant_type=password`);
      const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        cache: "no-store", headers: {
          'apikey': SUPABASE_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (!res.ok) {
        console.error('❌ Supabase signIn failed:', res.status, JSON.stringify(data));
        // Supabase v2 returns { code: 400, error_code: "invalid_credentials", msg: "..." }
        // Supabase v1 returns { error: "invalid_grant", error_description: "..." }
        return { 
          error: data.error_code || data.error || 'auth_failed', 
          error_description: data.msg || data.error_description || 'Credenciales inválidas' 
        };
      }
      console.log('✅ Supabase signIn success, user:', data.user?.id);
      return data;
    },

    // Alias para compatibilidad con Next.js / Supabase Auth v2
    signInWithPassword: async ({ email, password }) => {
      const data = await supabase.auth.signIn(email, password);
      if (data.error) return { data: null, error: data };
      return { data, error: null };
    },

    // OAuth real: redirige al endpoint de Supabase Auth que maneja el provider.
    // El callback (/auth/callback) procesa los tokens al volver.
    signInWithOAuth: ({ provider, options = {} }) => {
      if (typeof window === 'undefined') return;
      const redirectTo = options.redirectTo || `${window.location.origin}/auth/callback`;
      const params = new URLSearchParams({
        provider,
        redirect_to: redirectTo,
      });
      console.log(`🔌 OAuth redirect → ${provider}`);
      window.location.href = `${SUPABASE_URL}/auth/v1/authorize?${params.toString()}`;
    },
    
    signUp: async (email, password, metadata = {}) => {
      console.log('📝 Supabase signUp →', email);
      const res = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
        method: 'POST',
        cache: "no-store", headers: {
          'apikey': SUPABASE_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, data: metadata })
      });
      const data = await res.json();
      if (!res.ok) {
        console.error('❌ Supabase signUp failed:', res.status, JSON.stringify(data));
        return { 
          error: data.error_code || data.error || 'signup_failed', 
          error_description: data.msg || data.error_description || 'Error al registrar' 
        };
      }
      console.log('✅ Supabase signUp success');
      return data;
    },

    getUser: async (token) => {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        cache: "no-store", headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': `Bearer ${token}`
        }
      });
      return await res.json();
    },

    updateUser: async (token, data) => {
      console.log('🔄 Supabase updateUser →');
      const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: 'PUT',
        cache: "no-store", headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      const result = await res.json();
      if (!res.ok) {
        console.error('❌ Supabase updateUser failed:', res.status, JSON.stringify(result));
        return { error: result.msg || result.error || 'update_failed' };
      }
      return { data: result, error: null };
    },

    // ── MÓDULO ADMINISTRADOR (Bypass Rate Limits) ────────────────────
    admin: {
      createUser: async ({ email, password, email_confirm = true, user_metadata = {} }) => {
        const SERVICE_KEY = process.env.SUPABASE_SERVICE_ROLE_KEY?.trim();
        if (!SERVICE_KEY) throw new Error("SERVICE_ROLE_KEY missing");

        const res = await fetch(`${SUPABASE_URL}/auth/v1/admin/users`, {
          method: 'POST',
          headers: {
            'apikey': SERVICE_KEY,
            'Authorization': `Bearer ${SERVICE_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email,
            password,
            email_confirm,
            user_metadata
          })
        });

        const data = await res.json();
        if (!res.ok) return { data: null, error: data };
        return { data: { user: data }, error: null };
      }
    }
  },


  // ── BASE DE DATOS (REST) ───────────────────────────────────────────
  from: (table) => {
    return {
      select: (query = '*', options = {}) => {
        let filters = [];
        const builder = {
          eq: (column, value) => {
            filters.push(`${column}=eq.${encodeURIComponent(value)}`);
            return builder;
          },
          or: (filterString) => {
            filters.push(`or=(${filterString})`);
            return builder;
          },
          order: (column, { ascending = true } = {}) => {
            filters.push(`order=${column}.${ascending ? 'asc' : 'desc'}`);
            return builder;
          },
          limit: (n) => {
            filters.push(`limit=${n}`);
            return builder;
          },
          ilike: (column, pattern) => {
            filters.push(`${column}=ilike.${encodeURIComponent(pattern)}`);
            return builder;
          },
          range: (from, to) => {
            filters.push(`offset=${from}`);
            filters.push(`limit=${to - from + 1}`);
            return builder;
          },
          single: async () => {
            const queryParams = filters.length > 0 ? `&${filters.join('&')}` : '';
            const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?select=${query}${queryParams}`, {
              cache: "no-store", headers: {
                'apikey': SUPABASE_KEY,
                'Authorization': `Bearer ${SUPABASE_KEY}`,
                'Prefer': 'return=representation'
              }
            });
            const data = await res.json();
            if (!res.ok) return { data: null, error: data };
            const single = Array.isArray(data) ? data[0] || null : data;
            return { data: single, error: null };
          },
          maybeSingle: async () => {
            const queryParams = filters.length > 0 ? `&${filters.join('&')}` : '';
            const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?select=${query}${queryParams}`, {
              cache: "no-store", headers: {
                'apikey': SUPABASE_KEY,
                'Authorization': `Bearer ${SUPABASE_KEY}`,
                'Prefer': 'return=representation'
              }
            });
            if (!res.ok && res.status !== 406) {
              const err = await res.json().catch(() => ({}));
              return { data: null, error: err };
            }
            const data = await res.json().catch(() => null);
            const single = Array.isArray(data) ? data[0] || null : data;
            return { data: single, error: null };
          },
          execute: async () => {
            const queryParams = filters.length > 0 ? `&${filters.join('&')}` : '';
            const headers = {
              'apikey': SUPABASE_KEY,
              'Authorization': `Bearer ${SUPABASE_KEY}`,
              'Prefer': 'return=representation'
            };
            
            if (options.count === 'exact') {
              headers['Prefer'] = 'count=exact';
            }

            const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?select=${query}${queryParams}`, {
              method: options.head ? 'HEAD' : 'GET',
              headers
            });
            
            let data = [];
            if (!options.head) {
              try {
                data = await res.json();
              } catch (e) {
                data = [];
              }
            }

            if (!res.ok) return { data: null, error: data };

            let count = null;
            if (options.count === 'exact') {
              const range = res.headers.get('content-range');
              count = range ? parseInt(range.split('/')[1]) : 0;
            }

            return { data, count, error: null };
          },
          then: function(onFulfilled, onRejected) {
            return this.execute().then(onFulfilled, onRejected);
          }
        };
        return builder;
      },

      insert: async (data) => {
        const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
          method: 'POST',
          cache: "no-store", headers: {
            'apikey': SUPABASE_KEY,
            'Authorization': `Bearer ${SUPABASE_KEY}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation'
          },
          body: JSON.stringify(data)
        });
        const result = await res.json();
        if (!res.ok) return { data: null, error: result };
        return { data: result, error: null };
      },

      // NUEVO: Implementación de Builder para UPDATE
      update: (data) => {
        let filters = [];
        const builder = {
          eq: (column, value) => {
            filters.push(`${column}=eq.${encodeURIComponent(value)}`);
            return builder;
          },
          execute: async () => {
            const queryParams = filters.length > 0 ? `?${filters.join('&')}` : '';
            const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}${queryParams}`, {
              method: 'PATCH',
              cache: "no-store", headers: {
                'apikey': SUPABASE_KEY,
                'Authorization': `Bearer ${SUPABASE_KEY}`,
                'Content-Type': 'application/json',
                'Prefer': 'return=representation'
              },
              body: JSON.stringify(data)
            });
            const result = await res.json();
            if (!res.ok) return { data: null, error: result };
            return { data: result, error: null };
          },
          // Permitir 'await update().eq()' directamente
          then: function(onFulfilled, onRejected) {
            return this.execute().then(onFulfilled, onRejected);
          }
        };
        return builder;
      },

      delete: (match_or_filters) => {
        let filters = [];
        const builder = {
          eq: (column, value) => {
            filters.push(`${column}=eq.${encodeURIComponent(value)}`);
            return builder;
          },
          execute: async () => {
             const queryParams = filters.length > 0 ? `?${filters.join('&')}` : '';
             const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}${queryParams}`, {
               method: 'DELETE',
               cache: "no-store", headers: {
                 'apikey': SUPABASE_KEY,
                 'Authorization': `Bearer ${SUPABASE_KEY}`,
                 'Content-Type': 'application/json'
               }
             });
             if (!res.ok) {
               const error = await res.json().catch(() => ({ message: 'Delete failed' }));
               return { data: null, error };
             }
             return { data: true, error: null };
          },
          then: function(onFulfilled, onRejected) {
            return this.execute().then(onFulfilled, onRejected);
          }
        };
        // Soporte legacy para .delete({ id: 1 })
        if (match_or_filters && !match_or_filters.eq) {
           Object.keys(match_or_filters).forEach(k => builder.eq(k, match_or_filters[k]));
        }
        return builder;
      },

      upsert: async (data) => {
        const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
          method: 'POST',
          cache: "no-store", headers: {
            'apikey': SUPABASE_KEY,
            'Authorization': `Bearer ${SUPABASE_KEY}`,
            'Content-Type': 'application/json',
            'Prefer': 'resolution=merge-duplicates,return=representation'
          },
          body: JSON.stringify(data)
        });
        const result = await res.json();
        if (!res.ok) return { data: null, error: result };
        return { data: result, error: null };
      }
    };
  },

  storage: {
    from: (bucket) => ({
      upload: async (path, body, contentType) => {
        const res = await fetch(`${SUPABASE_URL}/storage/v1/object/${bucket}/${path}`, {
          method: 'POST',
          cache: "no-store", headers: {
            'apikey': SUPABASE_KEY,
            'Authorization': `Bearer ${SUPABASE_KEY}`,
            'Content-Type': contentType || 'application/octet-stream'
          },
          body: body
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) return { error: data || { message: 'Fallo subiendo archivo' } };
        return { data };
      },
      getPublicUrl: (path) => {
        return { data: { publicUrl: `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${path}` } };
      }
    })
  }
};
